import { Suspense, useDeferredValue, useMemo, useState } from 'react'
import { Image, AspectRatio, Stack, Loader } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { Typography } from '../common/typography'
import { useMetrics, useMetricsData } from '../widgets/api'
import { useRaces } from './api'
import { getRankSuffix } from '../common/helpers'
import { TeamLogo } from '../assets/api'
import classes from './Races.module.css'
import { Fmt } from '../widgets/format'
import { Dropdown } from '../common/components/dropdown/Dropdown'

interface KpiRoundResultsProps {
  competitionId: number
  roundNumber: number | null
  roundStart: string | undefined
  division?: number
}

export default function KpiRoundResults(props: KpiRoundResultsProps) {
  const { data: metrics } = useMetrics()
  const kpis = metrics.filter((m) => m.is_kpi && m.is_used_in_matches)

  const defaultKpi = useMemo(() => kpis[0]?.key ?? null, [kpis])

  const [selectedKpiState, setSelectedKpi] = useState<string | null>(null)
  const selectedKpi = selectedKpiState ?? defaultKpi
  const deferredSelectedKpi = useDeferredValue(selectedKpi)

  const kpiSelectData = useMemo(
    () =>
      kpis.map((kpi) => ({
        label: kpi.name,
        value: kpi.key,
      })),
    [kpis]
  )

  return (
    <>
      <div className={classes.kpiRoundHeader}>
        <Typography fw={500} fc={{ light: 'black', dark: 'gray-3' }} lh="125">
          Race KPI Rank
        </Typography>
        <Dropdown
          placeholder="Select KPI"
          value={selectedKpi}
          onChange={(value) => setSelectedKpi(value)}
          data={kpiSelectData}
          rightSection={<IconChevronDown className={classes.selectIcon} />}
          styles={{
            root: { maxWidth: '7rem' },
            input: { backgroundColor: 'transparent' },
          }}
        />
      </div>
      {deferredSelectedKpi && (
        <Suspense
          fallback={
            <Stack w="full" h="120px" justify="center" align="center">
              <Loader color="#0B6B7B" />
            </Stack>
          }
        >
          <div
            className={classes.kpiRoundBody}
            style={{
              opacity: deferredSelectedKpi !== selectedKpi ? 0.5 : 1,
            }}
          >
            <KpiRoundResultsInner
              {...props}
              selectedKpi={deferredSelectedKpi}
            />
          </div>
        </Suspense>
      )}
    </>
  )
}

interface TeamInfo {
  teamIds: number[]
  teamNameMap: Map<number, string>
  teamLogoMap: Map<number, TeamLogo>
}

function KpiRoundResultsInner({
  competitionId,
  roundNumber,
  roundStart,
  selectedKpi,
  division,
}: KpiRoundResultsProps & { selectedKpi: string }) {
  const { data: races } = useRaces({
    competition_id: competitionId,
    round_start: roundNumber || undefined,
    round_end: roundNumber || undefined,
    division,
  })

  const teamInfo = useMemo<TeamInfo>(() => {
    const uniqueTeamIds = new Set<number>()
    const nameMap = new Map<number, string>()
    const logoMap = new Map<number, TeamLogo>()

    races.forEach((race) => {
      race.results?.forEach((result) => {
        uniqueTeamIds.add(result.team_id)
        nameMap.set(result.team_id, result.team_name)
        logoMap.set(result.team_id, result.team_logo)
      })
    })

    return {
      teamIds: Array.from(uniqueTeamIds),
      teamNameMap: nameMap,
      teamLogoMap: logoMap,
    }
  }, [races])

  if (!roundStart || teamInfo.teamIds.length === 0) {
    return null
  }

  return (
    <KpiRoundResultsWithData
      roundStart={roundStart}
      selectedKpi={selectedKpi}
      competitionId={competitionId}
      teamInfo={teamInfo}
    />
  )
}

function KpiRoundResultsWithData({
  roundStart,
  selectedKpi,
  competitionId,
  teamInfo,
}: {
  roundStart: string
  selectedKpi: string
  competitionId: number
  teamInfo: TeamInfo
}) {
  const { teamIds, teamNameMap, teamLogoMap } = teamInfo

  const { data: kpiData } = useMetricsData({
    period_type: 'round',
    periods: [roundStart],
    period_range: false,
    subject_type: 'team',
    subject_ids: teamIds,
    metrics: [selectedKpi],
    components: ['percent'],
    statistics: ['value'],
    competition_id: competitionId,
  })

  const teamPercentMap = useMemo(() => {
    const map = new Map<number, number>()
    kpiData.forEach((data) => {
      map.set(data.subject, data.value)
    })
    return map
  }, [kpiData])

  const teamResults = useMemo(() => {
    return teamIds
      .map((teamId) => ({
        team_id: teamId,
        team_name: teamNameMap.get(teamId) || '',
        team_logo: teamLogoMap.get(teamId),
        percent: teamPercentMap.get(teamId) ?? 0,
      }))
      .sort((a, b) => b.percent - a.percent)
      .map((team, index) => ({
        ...team,
        rank: index + 1,
      }))
  }, [teamIds, teamNameMap, teamLogoMap, teamPercentMap])

  return (
    <>
      {teamResults.map((result) => (
        <div key={result.team_id} className={classes.kpiRoundRowWrapper}>
          <div>
            <Typography fs="sm" lh="140" ta="right">
              {result.rank}
              <sup>{getRankSuffix(result.rank)}</sup>
            </Typography>
            {result.team_logo && (
              <AspectRatio ratio={1 / 1} className={classes.kpiRoundTeamLogo}>
                <Image
                  src={result.team_logo.mini}
                  alt={result.team_name}
                  fit="scale-down"
                />
              </AspectRatio>
            )}
            <Typography fs="sm" lh="140">
              {result.team_name}
            </Typography>
          </div>
          <Typography fs="sm" fw={500} ff="JetBrains Mono" lh="140">
            <Fmt
              value={result.percent}
              metric={selectedKpi}
              component="percent"
              statistic="value"
            />
          </Typography>
        </div>
      ))}
    </>
  )
}
